import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { registerSW } from "virtual:pwa-register";
import { App } from "./App";
import { PwaProvider } from "./components/PwaProvider";
import "./styles.css";
import "./qr.css";

const hadServiceWorkerController = Boolean(navigator.serviceWorker?.controller);
let reloadingForUpdate = false;

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // Reload existing installations once the new worker owns the page. Do not
    // reload on the very first installation, where there was no old version.
    if (!hadServiceWorkerController || reloadingForUpdate) return;
    reloadingForUpdate = true;
    window.location.reload();
  });
}

let updateServiceWorker: (reloadPage?: boolean) => Promise<void>;
updateServiceWorker = registerSW({
  immediate: true,
  onNeedRefresh: () => void updateServiceWorker(true),
  onRegisteredSW: (_serviceWorkerUrl, registration) => {
    if (!registration) return;

    const checkForUpdate = () => {
      if (navigator.onLine) void registration.update();
    };

    // Installed PWAs can stay open for days. Check on every launch/focus and
    // periodically instead of waiting for the browser's implementation-defined
    // manifest/service-worker update schedule.
    checkForUpdate();
    window.setInterval(checkForUpdate, 30 * 60 * 1_000);
    window.addEventListener("focus", checkForUpdate);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") checkForUpdate();
    });
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: "online",
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Run once and fail visibly when the server is unavailable. Hotel
      // operations must never be queued and replayed later without the user.
      networkMode: "always",
      retry: 0,
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <PwaProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </PwaProvider>
    </QueryClientProvider>
  </StrictMode>,
);
